import { BaseComponent } from '../../lib/BaseComponent.js';

/**
 * Reusable Modal Component
 * Provides a flexible modal dialog for forms, confirmations, QR codes, etc.
 */
export class Modal extends BaseComponent {
  private isOpen = false;
  private title = '';
  private content: HTMLElement | null = null;
  private footer: HTMLElement | null = null;
  private onCloseCallback: (() => void) | null = null;
  private size: 'small' | 'medium' | 'large' = 'medium';
  private closeOnOverlay = true;

  /**
   * Open the modal with specified content
   */
  public open(options: {
    title: string;
    content: HTMLElement;
    footer?: HTMLElement;
    onClose?: () => void;
    size?: 'small' | 'medium' | 'large';
    closeOnOverlay?: boolean;
  }): void {
    this.title = options.title;
    this.content = options.content;
    this.footer = options.footer || null;
    this.onCloseCallback = options.onClose || null;
    this.size = options.size || 'medium';
    this.closeOnOverlay = options.closeOnOverlay !== undefined ? options.closeOnOverlay : true;
    this.isOpen = true;
    this.shadow.innerHTML = '';
    this.render();
  }

  /**
   * Close the modal
   */
  public close(): void {
    this.isOpen = false;
    this.shadow.innerHTML = '';
    this.render();
    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getStyles());

    if (!this.isOpen) {
      return;
    }

    const overlay = this.createElement('div', { className: 'modal-overlay' });
    if (this.closeOnOverlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.close();
        }
      });
    }

    const modal = this.createElement('div', { className: `modal-content modal-${this.size}` });

    // Header
    const header = this.createElement('div', { className: 'modal-header' });
    const titleEl = this.createElement('h3', {}, this.title);
    const closeBtn = this.createElement('button', { className: 'modal-close-btn', type: 'button' }, '×');
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(titleEl);
    header.appendChild(closeBtn);
    modal.appendChild(header);

    // Body
    const body = this.createElement('div', { className: 'modal-body' });
    if (this.content) {
      body.appendChild(this.content);
    }
    modal.appendChild(body);

    // Footer (optional)
    if (this.footer) {
      const footerEl = this.createElement('div', { className: 'modal-footer' });
      footerEl.appendChild(this.footer);
      modal.appendChild(footerEl);
    }

    overlay.appendChild(modal);
    this.shadow.appendChild(overlay);
  }

  private getStyles(): string {
    return `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        animation: fadeIn 0.2s ease-out;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .modal-content {
        background: white;
        border-radius: 8px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        animation: slideUp 0.3s ease-out;
      }

      @keyframes slideUp {
        from {
          transform: translateY(20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .modal-small {
        width: 90%;
        max-width: 400px;
      }

      .modal-medium {
        width: 90%;
        max-width: 600px;
      }

      .modal-large {
        width: 90%;
        max-width: 900px;
      }

      .modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .modal-header h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #111827;
      }

      .modal-close-btn {
        background: none;
        border: none;
        font-size: 32px;
        line-height: 1;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
      }

      .modal-close-btn:hover {
        background: #f3f4f6;
        color: #111827;
      }

      .modal-body {
        padding: 24px;
        overflow-y: auto;
        flex: 1;
      }

      .modal-footer {
        padding: 16px 24px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }

      @media (max-width: 640px) {
        .modal-content {
          width: 95%;
          max-height: 95vh;
        }

        .modal-header {
          padding: 16px;
        }

        .modal-body {
          padding: 16px;
        }

        .modal-footer {
          padding: 12px 16px;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('app-modal', Modal);
